// ============================================================
// C1：渲染预设面板（AI 推荐 + 一键应用/撤销/对比 + 自定义保存）
// 应用走 E0 命令总线事务（整批 1 个撤销步）；对比为临时直应用（不写历史）
// ============================================================
import { useCallback, useEffect, useState } from 'react';
import { Button, Drawer, Input, Modal, Space, Tag, Tooltip, message } from 'antd';
import { BgColorsOutlined, DeleteOutlined, ExperimentOutlined, SwapOutlined, UndoOutlined } from '@ant-design/icons';
import { commandBus } from '../../core/commandBus';
import { applyPresetDirect, buildPresetCommands, captureCurrentPresetSettings } from '../../utils/renderPresets';
import {
  createRenderPreset,
  deleteRenderPreset,
  fetchRenderPresets,
  recommendRenderPresets,
  type RenderPresetItem,
  type RenderPresetSettings,
} from '../../api/renderPresets';
import useSceneStore from '../../stores/sceneStore';

interface LastApply {
  preset: RenderPresetItem;
  prev: RenderPresetSettings;
  after: RenderPresetSettings;
}

export default function RenderPresetButton() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<RenderPresetItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [recommending, setRecommending] = useState(false);
  const [recommendSource, setRecommendSource] = useState<'llm' | 'heuristic' | null>(null);
  const [applyingKey, setApplyingKey] = useState<string | null>(null);
  const [lastApply, setLastApply] = useState<LastApply | null>(null);
  const [compareOn, setCompareOn] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveDesc, setSaveDesc] = useState('');
  const sceneName = useSceneStore((s) => s.currentScene?.name ?? '');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetchRenderPresets();
      setItems(r.items ?? []);
    } catch {
      message.error('获取渲染预设失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) void load();
  }, [open, load]);

  const applyPreset = async (preset: RenderPresetItem) => {
    const prev = captureCurrentPresetSettings();
    setApplyingKey(preset.key);
    try {
      const r = await commandBus.applyBatch(buildPresetCommands(preset.settings), { label: `preset:${preset.key}` });
      if (!r.ok) {
        message.error(r.error ?? '应用失败');
        return;
      }
      const after = captureCurrentPresetSettings();
      setLastApply({ preset, prev, after });
      setCompareOn(false);
      message.success(`已应用「${preset.name}」（可撤销）`);
    } finally {
      setApplyingKey(null);
    }
  };

  const undoApply = () => {
    commandBus.undo();
    setLastApply(null);
    setCompareOn(false);
    message.success('已撤销预设应用');
  };

  const toggleCompare = () => {
    if (!lastApply) return;
    if (!compareOn) {
      applyPresetDirect(lastApply.prev);
      setCompareOn(true);
    } else {
      applyPresetDirect(lastApply.after);
      setCompareOn(false);
    }
  };

  const doRecommend = async () => {
    setRecommending(true);
    try {
      const r = await recommendRenderPresets({ prompt, sceneName });
      const recKeys = new Set(r.items.map((i) => i.key));
      setItems((prev) => [...r.items, ...prev.filter((p) => !recKeys.has(p.key))]);
      setRecommendSource(r.source);
      message.success(r.source === 'llm' ? 'AI 推荐已生成' : '已按场景关键词推荐');
    } catch {
      message.error('推荐失败（可稍后重试）');
    } finally {
      setRecommending(false);
    }
  };

  const saveCustom = async () => {
    const name = saveName.trim();
    if (!name) {
      message.warning('请输入预设名称');
      return;
    }
    try {
      const created = await createRenderPreset({
        name,
        ...(saveDesc.trim() ? { description: saveDesc.trim() } : {}),
        settings: captureCurrentPresetSettings(),
      });
      setItems((prev) => [...prev, created]);
      setSaveOpen(false);
      setSaveName('');
      setSaveDesc('');
      message.success('已保存为自定义预设');
    } catch (e) {
      message.error((e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? '保存失败');
    }
  };

  const removeCustom = async (item: RenderPresetItem) => {
    if (!item.id) return;
    try {
      await deleteRenderPreset(item.id);
      setItems((prev) => prev.filter((p) => p.id !== item.id));
      message.success('已删除');
    } catch {
      message.error('删除失败');
    }
  };

  return (
    <>
      <Tooltip title="渲染预设（AI 推荐 / 一键应用 / 可撤销）">
        <Button size="small" icon={<BgColorsOutlined />} onClick={() => setOpen(true)}>
          渲染预设
        </Button>
      </Tooltip>

      <Drawer title="渲染预设" open={open} onClose={() => setOpen(false)} size={400} destroyOnHidden>
        <Space.Compact style={{ width: '100%', marginBottom: 12 }}>
          <Input
            placeholder="描述场景（如：夜景灯光巡检 / 雨天水务）"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onPressEnter={() => void doRecommend()}
            allowClear
          />
          <Button icon={<ExperimentOutlined />} loading={recommending} onClick={() => void doRecommend()}>
            AI 推荐
          </Button>
        </Space.Compact>
        {recommendSource ? (
          <div style={{ marginBottom: 8, fontSize: 12, color: '#8c8c8c' }}>
            推荐来源：{recommendSource === 'llm' ? 'AI 模型' : '场景关键词（AI 不可用时）'}
          </div>
        ) : null}

        {lastApply ? (
          <Space style={{ marginBottom: 12 }}>
            <Button size="small" icon={<UndoOutlined />} data-preset-action="undo" onClick={undoApply}>
              撤销应用
            </Button>
            <Button size="small" icon={<SwapOutlined />} onClick={toggleCompare}>
              {compareOn ? '退出对比' : `对比原设置`}
            </Button>
            <span style={{ fontSize: 12, color: '#8c8c8c' }}>当前：{lastApply.preset.name}</span>
          </Space>
        ) : null}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {items.map((item) => (
            <div
              key={item.key}
              data-preset-key={item.key}
              style={{
                border: '1px solid #f0f0f0',
                borderRadius: 8,
                padding: '10px 12px',
                background: item.reason ? '#f6faff' : '#fff',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Space size={6}>
                  <span style={{ fontWeight: 600 }}>{item.name}</span>
                  {item.builtin ? <Tag>内置</Tag> : <Tag color="blue">自定义</Tag>}
                  {item.reason ? <Tag color="cyan">推荐</Tag> : null}
                </Space>
                <Space size={4}>
                  <Button
                    size="small"
                    type="primary"
                    data-preset-action="apply"
                    loading={applyingKey === item.key}
                    onClick={() => void applyPreset(item)}
                  >
                    应用
                  </Button>
                  {!item.builtin && item.id ? (
                    <Button size="small" danger icon={<DeleteOutlined />} onClick={() => void removeCustom(item)} />
                  ) : null}
                </Space>
              </div>
              {item.reason ? <div style={{ fontSize: 12, color: '#1677ff', marginTop: 4 }}>{item.reason}</div> : null}
              <div style={{ fontSize: 12, color: '#595959', marginTop: 4 }}>{item.description}</div>
              <div style={{ marginTop: 6 }}>
                {item.tags.map((t) => (
                  <Tag key={t} style={{ fontSize: 11 }}>
                    {t}
                  </Tag>
                ))}
              </div>
            </div>
          ))}
          {!loading && items.length === 0 ? <div style={{ color: '#8c8c8c' }}>暂无预设</div> : null}
        </div>

        <div style={{ marginTop: 16 }}>
          <Button block onClick={() => setSaveOpen(true)}>
            保存当前设置为自定义预设
          </Button>
        </div>
      </Drawer>

      <Modal
        title="保存为自定义预设"
        open={saveOpen}
        onOk={() => void saveCustom()}
        onCancel={() => setSaveOpen(false)}
        destroyOnHidden
      >
        <Input
          placeholder="预设名称（≤40 字）"
          value={saveName}
          onChange={(e) => setSaveName(e.target.value)}
          style={{ marginBottom: 10 }}
        />
        <Input.TextArea
          placeholder="描述（可选）"
          rows={2}
          value={saveDesc}
          onChange={(e) => setSaveDesc(e.target.value)}
        />
      </Modal>
    </>
  );
}
